// Enhanced KeyFilter with organized key types
const KeyFilter = {
    // Organized key groups by type
    keyTypes: {
      // Navigation keys
      navigation: {
        arrows: [37, 38, 39, 40],  // Left, Up, Right, Down
        page: [33, 34, 35, 36],    // Page Up, Page Down, End, Home
        editing: [8, 46],          // Backspace, Delete
        tab: [9]                   // Tab
      },
      
      // Modifier keys
      modifiers: {
        basic: [16, 17, 18],       // Shift, Ctrl, Alt
        meta: [91, 92, 93]         // Windows/Command keys
      },
      
      // Function keys
      function: {
        standard: [112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123]  // F1-F12
      },
      
      // Control keys
      control: {
        lock: [20, 144, 145],      // Caps Lock, Num Lock, Scroll Lock
        system: [27, 19, 44, 45]   // Esc, Pause, Print Screen, Insert
      },
      
      // Character keys
      character: {
        letters: Array.from({length: 26}, (_, i) => i + 65),  // A-Z (65-90)
        numbers: Array.from({length: 10}, (_, i) => i + 48),  // 0-9 (48-57)
        numpad: Array.from({length: 10}, (_, i) => i + 96),   // Numpad 0-9 (96-105)
        punctuation: [
          186, 187, 188, 189, 190, 191, 192,  // ;=,-./ and `
          219, 220, 221, 222                  // [{\ and ]}' 
        ],
        space: [32],                          // Space
        enter: [13]                           // Enter
      },
      
      // Numpad operators
      numpad: {
        operators: [106, 107, 109, 110, 111]    // * + - . /
      }
    },
    
    // Predefined key sets (flattened for easy use)
    keySets: {},
    
    // Initialize the flattened key sets
    init: function() {
      // Flatten the nested structure for each category
      Object.keys(this.keyTypes).forEach(category => {
        this.keySets[category] = [];
        Object.values(this.keyTypes[category]).forEach(keyGroup => {
          this.keySets[category] = [...this.keySets[category], ...keyGroup];
        });
      });
      
      return this;
    },
    
    // Get a flattened array of all keys in a type group
    getTypeGroup: function(typeGroup) {
      if (!this.keyTypes[typeGroup]) {
        console.warn(`Type group "${typeGroup}" does not exist`);
        return [];
      }
      
      return Object.values(this.keyTypes[typeGroup]).flat();
    },
    
    // Get a specific subgroup of keys
    getKeyGroup: function(typeGroup, keyGroup) {
      if (!this.keyTypes[typeGroup]) {
        console.warn(`Type group "${typeGroup}" does not exist`);
        return [];
      }
      
      if (!this.keyTypes[typeGroup][keyGroup]) {
        console.warn(`Key group "${keyGroup}" does not exist in "${typeGroup}"`);
        return [];
      }
      
      return this.keyTypes[typeGroup][keyGroup];
    },
    
    // Custom key sets
    customSets: {},
    
    // Add a new key set or extend an existing one
    addKeySet: function(name, keys) {
      this.customSets[name] = Array.isArray(keys) ? keys : [keys];
      return this;
    },
    
    // Add keys to an existing set (custom or predefined)
    extendKeySet: function(name, keys) {
      const keyArray = Array.isArray(keys) ? keys : [keys];
      
      if (this.customSets[name]) {
        this.customSets[name] = [...this.customSets[name], ...keyArray];
      } else if (this.keySets[name]) {
        this.keySets[name] = [...this.keySets[name], ...keyArray];
      } else {
        this.addKeySet(name, keyArray);
      }
      
      return this;
    },
    
    // Build a combined filter from specified sets, types, or groups
    buildFilter: function(...specs) {
      let combinedKeys = [];
      
      specs.forEach(spec => {
        // If it's a string, check if it's a keySet, type group, or type:group notation
        if (typeof spec === 'string') {
          if (spec.includes(':')) {
            // Handle type:group notation (e.g., "navigation:arrows")
            const [typeGroup, keyGroup] = spec.split(':');
            combinedKeys = [...combinedKeys, ...this.getKeyGroup(typeGroup, keyGroup)];
          } else if (this.keySets[spec]) {
            // Handle key set
            combinedKeys = [...combinedKeys, ...this.keySets[spec]];
          } else if (this.keyTypes[spec]) {
            // Handle type group
            combinedKeys = [...combinedKeys, ...this.getTypeGroup(spec)];
          } else if (this.customSets[spec]) {
            // Handle custom set
            combinedKeys = [...combinedKeys, ...this.customSets[spec]];
          }
        } else if (Array.isArray(spec)) {
          // If it's an array, add the keys directly
          combinedKeys = [...combinedKeys, ...spec];
        }
      });
      
      // Remove duplicates
      return [...new Set(combinedKeys)];
    },
    
    // Check if a key should be ignored
    shouldIgnoreKey: function(e, specs = ['navigation', 'modifiers', 'function', 'control']) {
      const keysToIgnore = this.buildFilter(...specs);
      
      // Check if the key is in the ignore list
      if (keysToIgnore.includes(e.keyCode)) {
        return true;
      }
      
      // Check for modifier combinations
      if (e.ctrlKey || e.altKey || e.metaKey) {
        return true;
      }
      
      return false;
    }
  };
  
  KeyFilter.init();  // Initialize the key sets

// ---------------------------------------------- EXAMPLES - Start -----------------------------

// Example #1: Basic Use
// function format_attributeName(e) {
    
//     // Ignore navigation keys, modifiers, function keys, and control keys
//     if (KeyFilter.shouldIgnoreKey(e, ['navigation', 'modifiers', 'function', 'control'])) {
//       console.log("Ignoring non-content key:", e.keyCode);
//       return;
//     }
    
//     // Rest of code to be executed 
// }

// Example #2: Most compact approach: inline array directly in shouldIgnoreKey
// function inlineArrayExample(e) {
//     // Check if the pressed key is one of our specific keys
//     if (KeyFilter.shouldIgnoreKey(e, [[49, 52, 55]])) {
//       console.log("Ignoring specific key (1, 4, or 7)");
//       return;
//     }
    
//     // Rest of code to be executed 
//}

// Example #3: Alternative approach: directly using an array in buildFilter
// function directArrayExample(e) {
//     // Define the specific keys we want to ignore
//     const specificKeys = [49, 52, 55]; // Keys 1, 4, 7
    
//     // Check if the pressed key is in our specific array
//     if (KeyFilter.shouldIgnoreKey(e, [specificKeys])) {
//       console.log("Ignoring specific key (1, 4, or 7)");
//       return;
//     }
    
//      // Rest of code to be executed 
//}

// Example #4: Creating a simple custom key set with just specific keys
// function simpleCustomKeySet(e) {
//     // Create a custom key set with just keys 1, 4, 7 (keycodes 49, 52, 55)
//     KeyFilter.addKeySet('mySpecificKeys', [49, 52, 55]);
    
//     // Check if the pressed key is one of our specific keys
//     if (KeyFilter.shouldIgnoreKey(e, ['mySpecificKeys'])) {
//       console.log("Ignoring specific key (1, 4, or 7)");
//       return;
//     }
    
//     // Rest of code to be executed 
// }

// Example #5: Usage with the enhanced KeyFilter
// function formatWithSpecificGroups(e) {
//     // Ignore arrow keys, modifiers, and punctuation
//     if (KeyFilter.shouldIgnoreKey(e, ['navigation:arrows', 'modifiers', 'character:punctuation'])) {
//       console.log("Ignoring arrows, modifiers, or punctuation");
//       return;
//     }
    
//     // Rest of code to be executed 
// }
  
// Example #6: Creating a letters-only filter
// function lettersOnlyFilter(e) {
//     // Get all keys except letters
//     const nonLetterKeys = KeyFilter.buildFilter(
//       'navigation', 
//       'modifiers', 
//       'function', 
//       'control', 
//       // character:subkey will grant sub arrays
//       'character:numbers', 
//       'character:punctuation', 
//       'character:space', 
//       'numpadOps'
//     );
    
//     // Check if the key is not a letter key
//     if (nonLetterKeys.includes(e.keyCode) || e.ctrlKey || e.altKey || e.metaKey) {
//       if (![8, 46].includes(e.keyCode)) { // Allow backspace and delete for editing
//         console.log("Blocking non-letter key");
//         return;
//       }
//     }
    
//     // Rest of code to be executed 
// }
// ---------------------------------------------- EXAMPLES - End -----------------------------
